import React, { useState, useEffect } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Container,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Switch,
  Divider,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
} from '@mui/material';
import { ArrowBack, ChevronRight } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useMarketingConsentStatus, useUpdateMarketingConsent } from '../../hooks/useTerms';

const SECTION_LABEL_SX = {
  px: 1,
  mb: 1,
  fontSize: '0.8rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  color: 'text.secondary',
  textTransform: 'uppercase' as const,
};

const TERMS_LINKS = [
  { label: '서비스 이용약관', path: '/terms/SERVICE_TERMS' },
  { label: '개인정보처리방침', path: '/terms/PRIVACY_CONSENT' },
];

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const { isAgreed, loading: statusLoading } = useMarketingConsentStatus();
  const { update, loading: updateLoading } = useUpdateMarketingConsent();

  // 낙관적 업데이트용 로컬 상태
  const [localIsAgreed, setLocalIsAgreed] = useState(false);

  useEffect(() => {
    setLocalIsAgreed(isAgreed);
  }, [isAgreed]);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const handleMarketingToggle = async (checked: boolean) => {
    setLocalIsAgreed(checked); // 낙관적 반영
    try {
      await update(checked);
      setSnackbar({
        open: true,
        message: '마케팅 수신 설정이 변경되었습니다.',
        severity: 'success',
      });
    } catch {
      setLocalIsAgreed(!checked); // 실패 시 롤백
      setSnackbar({
        open: true,
        message: '설정 변경에 실패했습니다. 다시 시도해주세요.',
        severity: 'error',
      });
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate(-1)}
            aria-label="뒤로가기"
            sx={{ mr: 1 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
            설정
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ py: 3 }}>
        {/* ── 계정 정보 ─────────────────────────────── */}
        <Typography sx={SECTION_LABEL_SX}>계정 정보</Typography>
        <Paper variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
          <ListItem sx={{ py: 2 }}>
            <ListItemText
              primary={
                <Typography sx={{ fontSize: '1.05rem', fontWeight: 600 }}>
                  {(user as any)?.name ?? (user as any)?.userName ?? '사용자'}
                </Typography>
              }
              secondary={
                <Typography sx={{ fontSize: '0.95rem', color: 'text.secondary', mt: 0.25 }}>
                  {(user as any)?.email ?? ''}
                </Typography>
              }
            />
          </ListItem>
        </Paper>

        {/* ── 알림 설정 ─────────────────────────────── */}
        <Typography sx={SECTION_LABEL_SX}>알림 설정</Typography>
        <Paper variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
          <ListItem
            sx={{ py: 2, minHeight: 72, alignItems: 'flex-start' }}
            secondaryAction={
              statusLoading ? (
                <CircularProgress size={24} sx={{ mt: 0.5 }} />
              ) : (
                <Switch
                  checked={localIsAgreed}
                  onChange={(e) => handleMarketingToggle(e.target.checked)}
                  disabled={statusLoading || updateLoading}
                  sx={{
                    width: 58,
                    height: 42,
                    '& .MuiSwitch-switchBase': { padding: '9px' },
                    '& .MuiSwitch-thumb': { width: 24, height: 24 },
                  }}
                  inputProps={{ 'aria-label': '마케팅 정보 수신 동의' }}
                />
              )
            }
          >
            <ListItemText
              primary={
                <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>
                  마케팅 정보 수신
                </Typography>
              }
              secondary={
                <Typography
                  sx={{ fontSize: '0.875rem', color: 'text.secondary', mt: 0.5, lineHeight: 1.6, pr: 8 }}
                >
                  마케팅 정보 수신에 동의하시면 이벤트, 프로모션 등 유용한 정보를 받아보실 수 있습니다.
                </Typography>
              }
            />
          </ListItem>
        </Paper>

        {/* ── 약관 및 정책 ───────────────────────────── */}
        <Typography sx={SECTION_LABEL_SX}>약관 및 정책</Typography>
        <Paper variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
          <List disablePadding>
            {TERMS_LINKS.map((item, idx) => (
              <React.Fragment key={item.path}>
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  sx={{ py: 2, minHeight: 56 }}
                >
                  <ListItemText
                    primary={
                      <Typography sx={{ fontSize: '1rem' }}>{item.label}</Typography>
                    }
                  />
                  <ChevronRight sx={{ color: 'text.disabled' }} />
                </ListItemButton>
                {idx < TERMS_LINKS.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>

        {/* ── 로그아웃 ───────────────────────────────── */}
        <Button
          fullWidth
          variant="outlined"
          color="error"
          onClick={handleLogout}
          sx={{ py: 1.8, fontSize: '1rem', fontWeight: 600, borderRadius: 2, minHeight: 52 }}
        >
          로그아웃
        </Button>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ fontSize: '1rem' }}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsPage;
